/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AuthConfigurationV3 } from "@tivocorp/data-domain-lib";

import { Log } from "../../../model/logging";
import { DiagnosticLogger } from "../../../model/logging/DiagnosticLogger";
import { DiagnosticSeverity } from "../../../model/types";
import { SignInInteractor } from "../../../model/interactor";
import { DeviceModel } from "../../../model/device";
import { RetryMechanism } from "../../../model/retrymechanism";
import { localizedErrorFallback } from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import { createCriticalRetryOptions, toSignInErrorPayload } from "../common";
import type { SignInErrorPayload } from "../signIn.types";

const TAG: string = "signIn/authConfigThunks";

/**
 * App Startup — Step 2: Fetches auth configurations.
 * Requires global endpoints to have been fetched first
 * (via appStartup/fetchGlobalEndpoints).
 * Stores sorted authConfigurations and sets the default active tab.
 *
 * Uses CRITICAL retry — the overlay is shown on failure and the thunk
 * blocks until the auth configuration endpoint resolves. A definitive failure
 * (A100 / A101, HTTP 401 / 403) skips the retry and rejects with the catalog
 * code it carries, which the sign-in screen renders in its error alert —
 * without configs there is no sign-in form to fall back on.
 */
export const initializeSignIn = createAsyncThunk<
  AuthConfigurationV3[],
  { signal: AbortSignal },
  { rejectValue: SignInErrorPayload }
>(
  "signIn/initializeSignIn",
  async (
    { signal }: { signal: AbortSignal },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.d(TAG, "initializeSignIn() fetching auth configurations (v3)");
      const startTime: number = Date.now();

      const signInInteractor: SignInInteractor = new SignInInteractor();
      const authConfigResponse = await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchAuthConfigurationsV3({
            msoName: DeviceModel.getTiVoSettings().getMsoName()
          }),
        [],
        createCriticalRetryOptions(dispatch),
        signal
      );
      const configs: AuthConfigurationV3[] =
        authConfigResponse.authconfigurations !== undefined &&
        authConfigResponse.authconfigurations !== null
          ? [...authConfigResponse.authconfigurations].sort(
              (a: AuthConfigurationV3, b: AuthConfigurationV3): number =>
                a.displayRank - b.displayRank
            )
          : [];

      DiagnosticLogger.log(
        "pre_signin_api",
        "authConfigurations succeeded",
        DiagnosticSeverity.Info,
        {
          api: "authConfigurations",
          status: "success",
          latency_ms: String(Date.now() - startTime),
          config_count: String(configs.length)
        }
      );

      Log.d(
        TAG,
        "initializeSignIn() auth configs fetched, count: " +
          String(configs.length)
      );

      return configs;
    } catch (error: unknown) {
      const payload: SignInErrorPayload = toSignInErrorPayload(
        error,
        localizedErrorFallback(LocaleKeys.SIGN_IN_INITIALIZATION_ERROR_MESSAGE)
      );
      DiagnosticLogger.log(
        "pre_signin_api",
        "authConfigurations failed",
        DiagnosticSeverity.Error,
        {
          api: "authConfigurations",
          status: "failure",
          error_type:
            error instanceof Error ? error.constructor.name : "unknown",
          error_code: payload.code
        }
      );
      Log.e(TAG, "initializeSignIn() code=" + payload.code, error);
      return rejectWithValue(payload);
    }
  }
);
